/**
 * The time arithmetic behind "send this reply later" — pure, so it can be tested without a DOM.
 *
 * Times are BERLIN wall-clock, not the browser's zone: `berlinWallClockToIso` (views/rescheduleForm)
 * is the repo's existing answer for that, and it matters here because a recruiter on holiday abroad
 * picking "09:00" means nine in the morning for the candidate, not nine where the laptop is.
 */

/** `YYYY-MM-DDTHH:mm` in Berlin wall-clock, the shape <input type="datetime-local"> wants. */
export function toBerlinInputValue(date: Date): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  // en-CA gives ISO-ish date parts; hour can come back as "24" at midnight, which is not valid here.
  const hour = get('hour') === '24' ? '00' : get('hour');
  return `${get('year')}-${get('month')}-${get('day')}T${hour}:${get('minute')}`;
}

/**
 * The picker's granularity, in seconds — five minutes, matching the worker's tick.
 *
 * Offering a minute the scheduler cannot honour is a promise we do not keep: the tick runs every
 * five minutes, so 09:03 and 09:00 are the same send in practice. Snapping the field to the same
 * grid makes the choice honest.
 */
export const STEP_SECONDS = 300;

/**
 * The earliest selectable moment, rounded UP to the next five-minute boundary.
 *
 * ⚠️ Both halves matter. Rounding UP keeps `min` out of the past. And it must sit ON the grid,
 * because for `<input type="datetime-local">` the step base is taken from `min` — so a `min` of
 * 14:37 with a step of five minutes makes 14:37, 14:42, 14:47 the valid values, and every round
 * time the presets offer would be rejected as off-grid.
 */
export function earliestSelectable(now = new Date()): string {
  const ms = 5 * 60_000;
  return toBerlinInputValue(new Date(Math.ceil(now.getTime() / ms) * ms));
}

export interface Preset {
  key: string;
  label: string;
  /** Berlin wall-clock value for the picker. */
  value: string;
}

/**
 * The two presets, both at 09:00 Berlin.
 *
 * "Tomorrow" is the common case — a reply written late that should not land at 22:40. "Monday" is
 * the other one: replies written on a Friday afternoon. When today IS Monday, "Monday" means the
 * NEXT one, a week out, which is why it is labelled with its date.
 */
export function presetsFor(now = new Date()): Preset[] {
  const tomorrow = addBerlinDays(now, 1);
  const monday = nextBerlinMonday(now);
  return [
    { key: 'tomorrow', label: `Tomorrow, 09:00`, value: atBerlinNine(tomorrow) },
    {
      key: 'monday',
      label: `Monday ${labelForIsoDate(monday)}, 09:00`,
      value: atBerlinNine(monday),
    },
  ];
}

/** "14 Sep" for a plain y-m-d, read at midday UTC so no offset can move the day. */
function labelForIsoDate(isoDate: string): string {
  return berlinDayMonth(new Date(`${isoDate}T12:00:00.000Z`));
}

/** Berlin weekday index (0 = Sunday), read through the formatter so DST is not our problem. */
function berlinWeekday(date: Date): number {
  const name = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Berlin',
    weekday: 'short',
  }).format(date);
  return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(name);
}

function berlinDayMonth(date: Date): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: 'short',
  }).format(date);
}

/**
 * Add whole days to a Berlin CALENDAR date, on the date parts — not by adding 86_400_000 ms.
 *
 * ⚠️ Adding a day of milliseconds is wrong twice a year. From 00:30 Berlin on 25 October 2026, when
 * the clocks go back, +24h lands at 23:30 on the SAME date — so "tomorrow at 09:00" would resolve to
 * a time that has already passed, and "next Monday" could land on the Sunday while still being
 * labelled Monday. Incrementing the date parts cannot drift: `Date.UTC` normalises month and year
 * ends, and only the y-m-d is ever read back out.
 */
function addBerlinDays(date: Date, days: number): string {
  const [y, m, d] = toBerlinInputValue(date).slice(0, 10).split('-').map(Number);
  // Midday UTC as the carrier: far enough from either midnight that no offset can shift the date.
  const shifted = new Date(Date.UTC(y, m - 1, d + days, 12, 0, 0));
  return shifted.toISOString().slice(0, 10);
}

/** The next Monday strictly after today (so "Monday" on a Monday means a week away). */
function nextBerlinMonday(now: Date): string {
  const today = berlinWeekday(now);
  const ahead = ((8 - today) % 7) || 7;
  return addBerlinDays(now, ahead);
}

/** That Berlin date at 09:00, as a picker value. */
function atBerlinNine(isoDate: string): string {
  return `${isoDate}T09:00`;
}

/**
 * What the strip and the chip say. The five-minute tick means the real send lands between the chosen
 * time and five minutes after it, so the wording is approximate ON PURPOSE — promising the minute
 * would be a promise the scheduler does not keep.
 */
export function formatSendAt(iso: string, now = new Date()): string {
  const at = new Date(iso);
  const sameDay = berlinDayMonth(at) === berlinDayMonth(now);
  const time = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Berlin',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(at);
  if (sameDay) return `today ~${time}`;
  const day = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Berlin',
    weekday: 'short',
    day: '2-digit',
    month: 'short',
  }).format(at);
  return `${day} ~${time}`;
}

/** Recruiter-facing text for the short codes the server records on a failed row. */
export function describeFailure(code: string | null): string {
  switch (code) {
    case 'grant_revoked':
      return 'Gmail access was revoked, so it could not be sent. Reconnect Gmail and schedule again.';
    case 'missing_scope':
      return 'Gmail permissions are missing, so it could not be sent. Reconnect Gmail and schedule again.';
    case 'too_late':
      return 'It was more than 12 hours overdue, so it was not sent. Send it yourself if it still applies.';
    case 'thread_gone':
      return 'The conversation no longer has a message to reply to.';
    default:
      return 'It could not be sent. Send it yourself if it still applies.';
  }
}

/**
 * Recruiter-facing text for a reply that is DUE but being held back — still queued, not failed.
 * A held row used to look exactly like one nobody had reached yet, which is how a reply sat
 * "queued for ~08:00" all morning while the worker skipped it every five minutes.
 */
export function describeHold(code: string | null): string {
  switch (code) {
    case 'pilot_locked':
      return 'is due but paused by the pilot lock on this board. It goes out once the lock is lifted, or is marked not sent after 12 hours.';
    case 'pilot_posting_locked':
      // No "send it yourself": the card has no Send button while a reply is queued, and the
      // interactive send sits behind the same gate. Cancelling and re-scheduling from the card is
      // what records the candidate's posting, so that is the way out.
      return 'is due but paused for the pilot test — no test-pipeline posting is recorded for this candidate. Cancel it here and schedule it again from the card once they are on the test pipeline, or once the test ends; after 12 hours it is marked not sent.';
    default:
      return 'is due but on hold. It goes out when the hold clears, or is marked not sent after 12 hours.';
  }
}
